/**
 * BottomTabBar - Fixed bottom navigation bar for mobile viewports.
 *
 * Renders a row of five tab items (icon + label) pinned to the bottom of the
 * screen on mobile devices. The first four navigate to a core section of the
 * app; the fifth opens the "More" sheet:
 *	 - Dashboard (/)
 *	 - Orders (/work-orders)
 *	 - Inspect (/inspections)
 *	 - Settings (/profile)
 *	 - More — opens MoreMenuSheet with everything else (Buildings, Calendar,
 *	   Map, the project + recurring lists, and the role-gated Administration
 *	   group: Admin, CMS, API Keys). Without it those pages, admin tools
 *	   included, had no mobile entry point at all.
 *
 * Active tab detection uses React Router's useLocation() hook. The active tab
 * receives brand-primary colored icon and text. Inactive tabs are gray. When
 * the route belongs to none of the four tabs (e.g. /admin), "More" carries the
 * active state, since that's the tab the page was reached through.
 *
 * Visibility:
 *	 - Visible on mobile (below tablet breakpoint) via `block tablet:hidden`
 *	 - Hidden on tablet+ where the Sidebar takes over navigation
 *
 * Accessibility:
 *	 - Each tab has a minimum 44px touch target for comfortable tapping
 *	 - Includes safe area padding at the bottom for iPhone notch/home indicator
 *	 - Uses aria-current="page" on the active tab for screen readers
 *
 * This component does not accept props — it uses React Router internally.
 */
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MoreMenuSheet } from '../MoreMenuSheet';

/**
 * TabItem - Describes a single tab in the bottom navigation bar.
 *
 * @property label - Short display text shown below the icon
 * @property href  - Route path the tab navigates to
 * @property icon  - Render function that accepts a className string for dynamic coloring
 */
interface TabItem {
	label: string;
	href: string;
	icon: (className: string) => React.ReactNode;
}

/**
 * Determines whether a given tab's href matches the current pathname.
 * Uses exact match for root ("/") and startsWith for all others.
 */
const isActive = (href: string, pathname: string): boolean => {
	if (href === '/') {
		return pathname === '/';
	}
	return pathname.startsWith(href);
};

const BottomTabBar: React.FC<{ className?: string }> = ({ className }) => {
	/* ---------------------------------------------------------------
	 * Hooks
	 * --------------------------------------------------------------- */

	/** Current route location for active-state detection */
	const location = useLocation();

	/** Programmatic navigation for client-side routing */
	const navigate = useNavigate();

	/** Whether the "More" sheet is open (mobile secondary navigation) */
	const [moreOpen, setMoreOpen] = useState(false);

	/* Close the sheet whenever the route changes. Item taps close it directly,
	   but this also covers the back button and any in-page navigation that
	   happens while the sheet is up. */
	useEffect(() => {
		setMoreOpen(false);
	}, [location.pathname]);

	/* ---------------------------------------------------------------
	 * Tab definitions — icon + label + route for each tab
	 * --------------------------------------------------------------- */

	/**
	 * The four primary tabs shown in the mobile bottom bar.
	 * Each icon is a render function so we can pass dynamic color classes.
	 */
	const tabs: TabItem[] = [
		{
			label: 'Dashboard',
			href: '/',
			/* Grid/home icon — matches the sidebar Dashboard icon */
			icon: (cls: string) => (
				<svg className={cls} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25a2.25 2.25 0 0 1-2.25-2.25v-2.25Z" />
				</svg>
			),
		},
		{
			label: 'Orders',
			href: '/work-orders',
			/* Clipboard icon — matches the sidebar Work Orders icon */
			icon: (cls: string) => (
				<svg className={cls} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z" />
				</svg>
			),
		},
		{
			label: 'Inspect',
			href: '/inspections',
			/* Checklist icon — matches the sidebar Inspections icon */
			icon: (cls: string) => (
				<svg className={cls} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75" />
				</svg>
			),
		},
		{
			label: 'Settings',
			href: '/profile',
			/* Gear icon — matches the sidebar Settings icon */
			icon: (cls: string) => (
				<svg className={cls} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
					<path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
				</svg>
			),
		},
	];

	/* ---------------------------------------------------------------
	 * Click handler — uses React Router for client-side navigation
	 * --------------------------------------------------------------- */

	/**
	 * Navigates to the tab's route using React Router. Called on tab press.
	 * Also dismisses the "More" sheet if it happens to be open.
	 */
	const handleTabClick = (href: string) => {
		setMoreOpen(false);
		navigate(href);
	};

	/* ---------------------------------------------------------------
	 * Derived state
	 * --------------------------------------------------------------- */

	/**
	 * The "More" tab reads as active while its sheet is open, and also
	 * whenever the current route isn't behind one of the four routed tabs —
	 * /admin, /cms, /buildings and friends all live under "More", so leaving
	 * every tab unlit on those pages would look like a broken nav.
	 */
	const moreActive = moreOpen || !tabs.some((tab) => isActive(tab.href, location.pathname));

	/** Shared button classes for every tab — routed tabs and "More" alike */
	const tabButtonClasses = (active: boolean) => [
		/* BEM element + active modifier */
		'bottom-tab-bar__item',
		active ? 'bottom-tab-bar__item--active' : '',
		/* Flex column layout: icon above label */
		'flex flex-col items-center justify-center',
		/* Minimum 44px touch target for accessibility */
		'min-w-44px min-h-44px',
		/* Fill available space equally */
		'flex-1',
		/* Smooth color transitions */
		'transition-colors duration-200',
		/* Remove default button styles */
		'bg-transparent border-none outline-none',
		/* Focus ring for keyboard navigation */
		'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2',
	].join(' ');

	/** Shared label classes — active labels are slightly larger and bolder */
	const tabLabelClasses = (active: boolean) => [
		'font-sans mt-2px',
		active
			? 'text-11px font-semibold text-brand-primary'
			: 'text-10px text-brand-text-muted',
	].join(' ');

	/** Shared icon classes — the active tab's glyph is bumped up a few px */
	const tabIconClasses = (active: boolean) =>
		active ? 'w-24px h-24px text-brand-primary' : 'w-20px h-20px text-brand-text-muted';

	/* ---------------------------------------------------------------
	 * Component JSX
	 * --------------------------------------------------------------- */

	return (
		<>
			{/* ===== "More" sheet ===== */}
			{/* Rendered before the bar so the bar wins the stacking order and its
				tabs stay tappable while the sheet is open. The tab hrefs are handed
				over so the sheet can drop the links already on screen. */}
			<MoreMenuSheet
				isOpen={moreOpen}
				onClose={() => setMoreOpen(false)}
				excludeHrefs={tabs.map((tab) => tab.href)}
			/>

			<nav
				className={[
					/* BEM block marker */
					'bottom-tab-bar',
					/* Visible on mobile only — hidden on tablet+ where Sidebar is used */
					'block tablet:hidden',
					/* Fixed at the bottom of the viewport */
					'fixed bottom-0 left-0 right-0',
					/* White background with top border for separation from content */
					'bg-white border-t border-brand-border',
					/* High z-index to sit above page content — and above the
					   MoreMenuSheet's z-40, so the bar stays usable while it's open */
					'z-50',
					/* Safe area padding for iPhone home indicator / notch area */
					'pb-[env(safe-area-inset-bottom,0px)]',
					/* Forwarded extra classes */
					className ?? '',
				].join(' ')}
				data-ref="organism-bottom-tab-bar"
				aria-label="Mobile navigation"
			>
				{/* Tab row: evenly distributed flex items */}
				<div className="flex items-center justify-around h-64px">
					{tabs.map((tab) => {
						const active = isActive(tab.href, location.pathname);

						return (
							<button
								key={tab.href}
								type="button"
								onClick={() => handleTabClick(tab.href)}
								className={tabButtonClasses(active)}
								aria-current={active ? 'page' : undefined}
								aria-label={tab.label}
							>
								{/* Tab icon — sized slightly larger when active for emphasis */}
								{tab.icon(tabIconClasses(active))}
								{/* Tab label text */}
								<span className={tabLabelClasses(active)}>{tab.label}</span>
							</button>
						);
					})}

					{/* ===== "More" tab ===== */}
					{/* Toggles the MoreMenuSheet rather than navigating — it's the
						only way onto Buildings/Calendar/Map and the Administration
						pages (Admin, CMS, API Keys) from a phone. */}
					<button
						type="button"
						onClick={() => setMoreOpen((open) => !open)}
						className={tabButtonClasses(moreActive)}
						aria-label="More"
						aria-expanded={moreOpen}
						aria-haspopup="dialog"
					>
						{/* Horizontal ellipsis icon — the conventional "more" glyph */}
						<svg className={tabIconClasses(moreActive)} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" d="M6.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM18.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
						</svg>
						<span className={tabLabelClasses(moreActive)}>More</span>
					</button>
				</div>
			</nav>
		</>
	);
};

export default BottomTabBar;
